/**
 * Perplexity — the first acquisition:'ambient' connector profile.
 *
 * Miro / Notion / Linear / Jira are `acquisition: 'api'` — they import items.
 * The ambient lane is different: an ambient connector does not import items at
 * all, it WATCHES the outside world and contributes external entities for
 * Beacon to monitor. Perplexity (research/search over the live web) is the
 * natural first occupant of that lane.
 *
 * This profile is the declaration/contract the subsystem reserved (02-DESIGN
 * §6, 06-SIGNAL-VOCABULARY-CROSSWALK §3): "a connector feeds Beacon's watchlist
 * instead of Beacon being RSS-only for its inputs." The data seam is
 * `registerWatchlistEntities` (beaconWatchlist.ts); the entity provenance is
 * `beacon_watchlist.origin='connector'` (migration 608). A concrete Perplexity
 * fetch that extracts watch-entities is the follow-on; the lane, the seam, and
 * the provenance are what this profile makes real.
 *
 * The four preservation planes are all `none`: an ambient connector moves no
 * items, so there is nothing to preserve in the item graph. What it preserves
 * lives in Beacon's watchlist (the entity + its kind + its source_refs), not
 * here — the notes say so on each plane rather than overstating fidelity.
 */

import type { ConnectorProfile } from '../profileTypes';

export const PERPLEXITY_PROFILE: ConnectorProfile = {
  connectorType: 'perplexity',
  profileVersion: 1,

  nativeObjectTypes: ['watch_entity'],
  relationshipModel: 'none',
  visualChannels: [],
  spatialModel: 'none',
  acquisition: 'ambient',

  fidelity: {
    content: {
      export: 'none',
      import: 'none',
      notes: 'Ambient: no item content is imported. Watched entities (name + kind) land in Beacon’s watchlist via registerWatchlistEntities, not the item graph.',
    },
    structure: {
      export: 'none',
      import: 'none',
      notes: 'Ambient: no structure — the connector contributes flat watch entities, not a hierarchy.',
    },
    relationships: {
      export: 'none',
      import: 'none',
      notes: 'Ambient: no item relationships. An entity’s source_refs bind it to the plan artifacts it underpins (the materiality key), which is Beacon’s concern, not a canonical edge.',
    },
    semantics: {
      export: 'none',
      import: 'none',
      notes: 'Ambient: no item category. The entity kind (competitor / technology / regulation / customer / market) is Beacon’s vocabulary, recorded on the watchlist row.',
    },
  },

  identity: { stableExternalIds: false, deepLinks: false, appMetadata: false },
  continuity: { webhooks: false, comments: false },
};
